// src/nexus/learningPulse.js
//
// Learning-world extractor for the Learning Ledger cockpit. Mirrors the
// validate-or-null discipline established in systemPulse.js/
// operationsPulse.js: a field is only trusted once the owning endpoint's own
// classifyEndpointResult() state says "live"/"partial" and the specific
// nested field has the expected type — otherwise null, never a guessed or
// fabricated value.
//
// Root cause this closes: /api/admin/learning-diagnostics is already fetched
// by the "learning" world (LiveWorldPanel), and the six matching frontend
// fetch wrappers in src/services/api.js exist, but nothing ever parsed the
// response into LearningView — its real learningUpdates/memoryUpdates/
// featureHistory/discoveryLabels/outcomeTrackingReadiness numbers never
// reached the UI at all.
//
// Verified against the real backend response shape by reading
// services/learningDiagnostics.service.js#getLearningDiagnostics and its
// route handler directly (davidhuecker-bit/hqs-backend):
//   GET /api/admin/learning-diagnostics ->
//     { success, learningUpdates, memoryUpdates, metaLearningUpdates,
//       lastRuns[], errors[], generatedAt,
//       data: { featureHistory: { totalRows, rowsLast24h, rowsLast7d,
//         activeSymbols, lastActivity, indicatorCounts[] },
//         discoveryLabels: { totalRows, rowsLast24h, rowsLast7d,
//         successCount, failCount, lastSignalTime, successRate },
//         outcomeTrackingReadiness: { totalRows, rowsMature20d,
//         rowsMature30d, lastCapture }, status, message } }

const USABLE_ENDPOINT_STATES = new Set(["live", "partial"]);

function findEndpoint(endpointResults, id) {
  if (!Array.isArray(endpointResults)) return null;
  return endpointResults.find((entry) => entry && entry.id === id) || null;
}

function isUsable(endpoint) {
  return !!endpoint && USABLE_ENDPOINT_STATES.has(endpoint.state);
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeInteger(value) {
  return isFiniteNumber(value) && Number.isInteger(value) && value >= 0;
}

function endpointBody(endpoint) {
  if (!endpoint) return null;
  const raw = endpoint.data;
  return isPlainObject(raw) ? raw : null;
}

function pushMissing(missingFields, field) {
  if (!missingFields.includes(field)) missingFields.push(field);
}

export function extractLearningDiagnostics(endpointResults, missingFields = []) {
  const endpoint = findEndpoint(endpointResults, "diagnostics");
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "learningDiagnostics");
    return null;
  }
  const body = endpointBody(endpoint);
  if (!body) return null;
  const nested = isPlainObject(body.data) ? body.data : {};
  const featureHistory = isPlainObject(nested.featureHistory) ? nested.featureHistory : null;
  const discoveryLabels = isPlainObject(nested.discoveryLabels) ? nested.discoveryLabels : null;
  const outcomeTrackingReadiness = isPlainObject(nested.outcomeTrackingReadiness) ? nested.outcomeTrackingReadiness : null;
  return {
    learningUpdates: isNonNegativeInteger(body.learningUpdates) ? body.learningUpdates : null,
    memoryUpdates: isNonNegativeInteger(body.memoryUpdates) ? body.memoryUpdates : null,
    metaLearningUpdates: isNonNegativeInteger(body.metaLearningUpdates) ? body.metaLearningUpdates : null,
    status: typeof nested.status === "string" ? nested.status : null,
    message: typeof nested.message === "string" ? nested.message : null,
    featureHistory: featureHistory ? {
      totalRows: isNonNegativeInteger(featureHistory.totalRows) ? featureHistory.totalRows : null,
      rowsLast24h: isNonNegativeInteger(featureHistory.rowsLast24h) ? featureHistory.rowsLast24h : null,
      activeSymbols: isNonNegativeInteger(featureHistory.activeSymbols) ? featureHistory.activeSymbols : null,
      lastActivity: typeof featureHistory.lastActivity === "string" ? featureHistory.lastActivity : null,
    } : null,
    discoveryLabels: discoveryLabels ? {
      totalRows: isNonNegativeInteger(discoveryLabels.totalRows) ? discoveryLabels.totalRows : null,
      rowsLast7d: isNonNegativeInteger(discoveryLabels.rowsLast7d) ? discoveryLabels.rowsLast7d : null,
      successRate: isFiniteNumber(discoveryLabels.successRate) ? discoveryLabels.successRate : null,
    } : null,
    outcomeTrackingReadiness: outcomeTrackingReadiness ? {
      totalRows: isNonNegativeInteger(outcomeTrackingReadiness.totalRows) ? outcomeTrackingReadiness.totalRows : null,
      rowsMature20d: isNonNegativeInteger(outcomeTrackingReadiness.rowsMature20d) ? outcomeTrackingReadiness.rowsMature20d : null,
    } : null,
  };
}

export function normalizeLearningPulse({ endpointResults } = {}) {
  const missingFields = [];
  const diagnostics = extractLearningDiagnostics(endpointResults, missingFields);
  return { diagnostics, missingFields };
}
